import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from 'src/firebase/firebaseConfig';

export type EventDateEntry = { date: string | null } | 'loading' | 'error';

/**
 * Live canonical event dates (events/{id}.dateISO8601): one onSnapshot per distinct eventId.
 * Ids without an entry yet are 'loading'.
 */
export function useEventDates(eventIds: Array<string | undefined | null>): Map<string, EventDateEntry> {
  const [entries, setEntries] = useState<Record<string, EventDateEntry>>({});

  const key = useMemo(
    () => Array.from(new Set(eventIds.filter((id): id is string => !!id))).sort().join('|'),
    [eventIds]
  );

  useEffect(() => {
    const ids = key ? key.split('|') : [];
    const unsubs = ids.map((id) =>
      onSnapshot(
        doc(db, 'events', id),
        (snap) => {
          const raw = snap.exists() ? snap.data()?.dateISO8601 : null;
          setEntries((prev) => ({ ...prev, [id]: { date: typeof raw === 'string' && raw ? raw : null } }));
        },
        (error) => {
          console.error('Error reading event date:', id, error);
          setEntries((prev) => ({ ...prev, [id]: 'error' }));
        }
      )
    );
    return () => unsubs.forEach((unsub) => unsub());
  }, [key]);

  return useMemo(() => {
    const map = new Map<string, EventDateEntry>();
    if (key) key.split('|').forEach((id) => map.set(id, entries[id] ?? 'loading'));
    return map;
  }, [key, entries]);
}
